import React, { Fragment, useEffect, useState } from "react";
import {
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Grid,
  GridItem,
  Text,
  Tooltip,
  Checkbox,
  FormLabel,
  HStack,
  ButtonGroup,
  Button,
  useBoolean,
  Input,
} from "@chakra-ui/react";
import uniqid from "uniqid";
import { DateTime } from "luxon";
import color from "../../config/color";
import { fetchWithToken } from "../../functions/fetch";
import {
  createFeeSchema,
  editFeeSchema,
} from "../../validation/leaseValidation";
import { useQueryClient } from "@tanstack/react-query";
import { scooterFeeType } from "../../config/config";

const FeePage = ({
  fees,
  setSubmitFunction,
  currentLease,
  pageIndex,
  setIsSubmitting,
}) => {
  const titles = ["房間租金", `${scooterFeeType}`, "車位費", "電費", "其他"];
  const feeFields = ["費用名稱", "繳費期限", "金額", "繳交狀態", "備註"];
  const [creating, setCreating] = useBoolean();
  const [errorMessages, setErrorMessages] = useState([]);
  const [createFeeValues, setCreateFeeValues] = useState({
    name: "",
    deadline: "",
    cost: "0",
    paid: false,
    note: "",
  });

  const [updateFeeValues, setUpdateFeeValues] = useState({
    ids: [],
    costs: [],
    paidArr: [],
    notes: [],
    shouldUpdate: [],
  });
  const [editing, setEditing] = useBoolean();
  const [tabIndex, setTabIndex] = useState(0);

  const queryClient = useQueryClient();

  const updateDefaultUpdateFeeValues = (index) => {
    const title = titles[index];
    const filteredFees = fees.filter((fee) => fee.type === title);

    const idArray = [];
    const costArray = [];
    const paidArray = [];
    const noteArray = [];
    const shouldUpdate = [];

    filteredFees.forEach((fee) => {
      idArray.push(fee._id);
      costArray.push(fee.cost);
      paidArray.push(fee.paid);
      noteArray.push(fee.note);
      shouldUpdate.push(false);
    });

    setUpdateFeeValues({
      ids: idArray,
      costs: costArray,
      paidArr: paidArray,
      notes: noteArray,
      shouldUpdate,
    });
  };

  const createFee = async () => {
    setIsSubmitting(true);
    try {
      await createFeeSchema.validate(createFeeValues);
    } catch (error) {
      setErrorMessages(error.errors);
      setIsSubmitting(false);
      return;
    }
    const res = await fetchWithToken(
      "POST",
      `${process.env.REACT_APP_BACKEND_HOST}/api/lease/${currentLease._id}/fee`,
      createFeeValues,
    );

    if (res.status === 200) {
      queryClient.invalidateQueries({ queryKey: ["fees", currentLease._id] });
      setErrorMessages([]);
      setCreating.off();
    } else {
      console.error(res);
    }
    setIsSubmitting(false);
  };

  const updateFee = async () => {
    setIsSubmitting(true);
    // what indexes to update
    const indexes = [];
    const { costs, shouldUpdate, paidArr, notes, ids } = updateFeeValues;
    shouldUpdate.forEach((val, index) => {
      if (val) {
        indexes.push(index);
      }
    });

    // use for loop for async await
    for (let i = 0; i < indexes.length; i++) {
      const index = indexes[i];
      const body = {
        cost: costs[index],
        paid: paidArr[index],
        note: notes[index],
      };
      try {
        await editFeeSchema.validate(body);
      } catch (err) {
        setErrorMessages(err.errors);
        setIsSubmitting(false);
        return;
      }
      const res = await fetchWithToken(
        "PUT",
        `${process.env.REACT_APP_BACKEND_HOST}/api/fee/${ids[index]}`,
        body,
      );
      // don't stop if one of them is not edited
      if (res.status !== 200) {
        console.error(res);
      }
    }

    queryClient.invalidateQueries({ queryKey: ["fees", currentLease._id] });
    setErrorMessages([]);
    setEditing.off();
    setIsSubmitting(false);
  };

  const updateCreateFeeValues = (e, field) => {
    const { value } = e.target;
    setCreateFeeValues((prev) => {
      prev[field] = value;
      return prev;
    });
  };

  const updateUpdateFeeValues = (e, index, field) => {
    const { value } = e.target;
    setUpdateFeeValues((prev) => {
      prev[field][index] = value;
      prev.shouldUpdate[index] = true; // this fee is touched, should be updated
      return prev;
    });
  };

  useEffect(() => {
    // only change function when this page is focused
    if (pageIndex !== 1) {
      return;
    }
    if (creating && !editing) {
      setSubmitFunction(() => createFee);
    } else if (!creating && editing) {
      setSubmitFunction(() => updateFee);
    } else {
      setSubmitFunction(() => () => {});
    }
  }, [creating, editing, pageIndex]);

  useEffect(() => {
    // set the default value of update fee values (tab 0)
    // NOTE: wait until fee is ready
    if (fees?.length <= 0) {
      return;
    }
    updateDefaultUpdateFeeValues(tabIndex);
  }, [fees, tabIndex]);

  return (
    <Tabs index={tabIndex} onChange={(index) => setTabIndex(index)}>
      <TabList>
        {titles.map((title) => (
          <Tab key={uniqid()}>{title}</Tab>
        ))}
      </TabList>
      <TabPanels>
        {titles.map((title) => {
          const filteredFees = fees.filter((fee) => fee.type === title);

          return (
            <TabPanel key={uniqid()} px={0}>
              <Grid
                templateColumns="repeat(5, minmax(auto, 200px))"
                textAlign="center"
                whiteSpace="nowrap"
                gap={4}
              >
                {feeFields.map((field) => (
                  <GridItem key={uniqid()} w="100%" fontWeight="bold">
                    {field}
                  </GridItem>
                ))}
                {filteredFees.length === 0 ? (
                  <GridItem colSpan={5} textAlign="start">
                    無費用
                  </GridItem>
                ) : (
                  filteredFees.map((fee, index) => (
                    <Fragment key={uniqid()}>
                      <GridItem>{fee?.name}</GridItem>
                      <GridItem>
                        {DateTime.fromISO(fee?.deadline).toFormat("yyyy/MM/dd")}
                      </GridItem>
                      <GridItem>
                        {editing ? (
                          <Input
                            variant="filled"
                            size="sm"
                            type="number"
                            defaultValue={updateFeeValues.costs[index]}
                            onChange={(e) =>
                              updateUpdateFeeValues(e, index, "costs")
                            }
                          />
                        ) : (
                          fee?.cost
                        )}
                      </GridItem>
                      <GridItem>
                        <HStack justify="center">
                          {/* use original/edited default value */}
                          <Checkbox
                            defaultChecked={
                              editing
                                ? updateFeeValues.paidArr[index]
                                : fee?.paid
                            }
                            disabled={!editing}
                            id={`paid${fee._id}`}
                            onChange={(e) => {
                              setUpdateFeeValues((prev) => {
                                prev.paidArr[index] = e.target.checked;
                                prev.shouldUpdate[index] = true;
                                return prev;
                              });
                            }}
                          />
                          <FormLabel m={0} htmlFor={`paid${fee._id}`}>
                            已繳費
                          </FormLabel>
                        </HStack>
                      </GridItem>
                      <GridItem>
                        {editing ? (
                          <Input
                            defaultValue={updateFeeValues.notes[index]}
                            onChange={(e) => {
                              updateUpdateFeeValues(e, index, "notes");
                            }}
                            variant="filled"
                            size="sm"
                          />
                        ) : (
                          <Tooltip
                            label={fee?.note}
                            hasArrow
                            placement="bottom"
                          >
                            <Text overflow="hidden" textOverflow="ellipsis">
                              {fee?.note}
                            </Text>
                          </Tooltip>
                        )}
                      </GridItem>
                    </Fragment>
                  ))
                )}
                {creating && title === "其他" && (
                  <>
                    {/* use default value to prevent data disappear after invalid submission */}
                    <Input
                      variant="filled"
                      defaultValue={createFeeValues.name}
                      onChange={(e) => updateCreateFeeValues(e, "name")}
                    />
                    <Input
                      variant="filled"
                      defaultValue={createFeeValues.deadline}
                      onChange={(e) => updateCreateFeeValues(e, "deadline")}
                      type="date"
                    />
                    <Input
                      variant="filled"
                      defaultValue={createFeeValues.cost}
                      onChange={(e) => updateCreateFeeValues(e, "cost")}
                      type="number"
                    />
                    <HStack justify="center">
                      <Checkbox
                        id="new_paid"
                        defaultChecked={createFeeValues.paid}
                        onChange={(e) =>
                          setCreateFeeValues((prev) => {
                            prev.paid = e.target.checked;
                            return prev;
                          })
                        }
                      />
                      <FormLabel m={0} htmlFor="new_paid">
                        已繳費
                      </FormLabel>
                    </HStack>
                    <Input
                      variant="filled"
                      defaultValue={createFeeValues.note}
                      onChange={(e) => updateCreateFeeValues(e, "note")}
                    />
                  </>
                )}
                {/* create and edit button */}
                <GridItem textAlign="start" colSpan={5}>
                  <ButtonGroup size="sm">
                    {title === "其他" && (
                      <Button
                        colorScheme="facebook"
                        onClick={() => {
                          setCreating.on();
                          setEditing.off();
                        }}
                      >
                        新增
                      </Button>
                    )}
                    {filteredFees.length > 0 && (
                      <Button
                        colorScheme="orange"
                        bgColor={color.orange}
                        color="white"
                        onClick={() => {
                          setEditing.toggle();
                          setCreating.off();
                        }}
                      >
                        修改
                      </Button>
                    )}
                  </ButtonGroup>
                </GridItem>
                <GridItem colSpan={5}>
                  {errorMessages.map((message) => (
                    <Text color="red.500" colSpan={5} key={uniqid()}>
                      {message}
                    </Text>
                  ))}
                </GridItem>
              </Grid>
            </TabPanel>
          );
        })}
      </TabPanels>
    </Tabs>
  );
};

export default FeePage;
